import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@material-ui/core";

import { setActiveDialog } from "src/store/adminHtml/actions";

import {
  getUnreviewedReturnSales,
  getSaleById,
  getSalesLoading,
} from "src/store/sale/selector";

import {
  updateItem,
  updateBundledItems,
  moveItem,
  moveBundledItems,
  recordReturn,
} from "src/store/sale/actions";
import { EditDialog } from "src/pages/SalesPage/EditDialog";
import { Sale } from "src/interfaces/sale.interface";
import { parseError } from "src/utils";
import ReviewQueueStatus from "src/components/ReviewQueueStatus";
import { useReviewQueueLoader } from "src/utils/userData/useReviewQueueLoader";

import ConfirmReturnDetails from "./ConfirmReturnDetails";

function SaleEditDialog({
  returnSale,
  onClose,
}: {
  returnSale?: Sale;
  onClose: () => void;
}) {
  const dispatch = useDispatch();
  const editSale = useSelector((state: any) =>
    getSaleById(state, returnSale?.original_id)
  );
  const loading = useSelector(getSalesLoading);

  return (
    <EditDialog
      loading={loading}
      open={!!editSale}
      data={editSale}
      onClose={onClose}
      onSave={async (item) => {
        try {
          await dispatch(updateItem(item));
          toast.success("Sale has been updated successfully");
        } catch (error) {
          toast.error(parseError(error));
        }
      }}
      onSaveBundle={async (items) => {
        try {
          await dispatch(updateBundledItems(items));
          toast.success("Sale has been updated successfully");
        } catch (error) {
          toast.error(parseError(error));
        }
      }}
      onMove={async (item) => {
        try {
          await dispatch(moveItem(item));
          toast.success("Sale has been updated successfully");
        } catch (error) {
          toast.error(parseError(error));
        }
      }}
      onMoveBundle={async (items) => {
        try {
          await dispatch(moveBundledItems(items));
          toast.success("Sale has been updated successfully");
        } catch (error) {
          toast.error(parseError(error));
        }
      }}
      onReturn={async (returnRecord: any, sale: any, inventory: any) => {
        try {
          await dispatch(recordReturn({ returnRecord, sale, inventory }));
          toast.success("Sale has been updated successfully");
        } catch (error) {
          toast.error(parseError(error));
        }
      }}
      onDelete={() =>
        console.error(
          "onDelete - should never happen (user shouldn't be allow to do this action)"
        )
      }
      onDeleteBundle={() =>
        console.error(
          "onDeleteBundle - should never happen (user shouldn't be allow to do this action)"
        )
      }
      onDeleteReturn={() =>
        console.error(
          "onDeleteReturn - should never happen (user shouldn't be allow to do this action)"
        )
      }
      editReturn={() => onClose()}
    />
  );
}

function ReviewReturnsDialogContent({
  onClose,
  queueLoading,
  queueError,
  onRetry,
}: {
  onClose: () => void;
  queueLoading: boolean;
  queueError: unknown;
  onRetry: () => void;
}) {
  const unreviewedReturns = useSelector(getUnreviewedReturnSales);
  const [editReturnSale, setEditReturnSale] = useState<Sale>();

  if (queueLoading || queueError) {
    return (
      <>
        <DialogTitle>Confirm Refund Details</DialogTitle>
        <ReviewQueueStatus
          loading={queueLoading}
          error={queueError}
          onRetry={onRetry}
          label="refunds to review"
        />
      </>
    );
  }

  return (
    <>
      <SaleEditDialog
        returnSale={editReturnSale}
        onClose={() => setEditReturnSale(undefined)}
      />
      {unreviewedReturns.length > 0 && (
        <ConfirmReturnDetails
          onClose={onClose}
          unreviewedReturn={unreviewedReturns[0]}
          unreviewedReturns={unreviewedReturns.length}
          onGoToSale={(r) => setEditReturnSale(r)}
        />
      )}
      {unreviewedReturns.length === 0 && (
        <>
          <DialogTitle>Confirm Refund Details</DialogTitle>
          <DialogContent>
            <DialogContentText className="relative" tabIndex={-1}>
              <Typography variant="body1" paragraph>
                Congratulations!
              </Typography>
              <Typography variant="body1" paragraph>
                You have finished reviewing all the refunds.
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions className="px-7 py-5">
            <Button color="primary" variant="contained" onClick={onClose}>
              Done
            </Button>
          </DialogActions>
        </>
      )}
    </>
  );
}

function ReviewReturnsDialog({ open }: { open: boolean }) {
  const dispatch = useDispatch();
  const queue = useReviewQueueLoader(open, "sales");
  const handleClose = () => {
    dispatch(setActiveDialog(""));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="body"
      fullWidth
      maxWidth="sm"
    >
      <ReviewReturnsDialogContent
        onClose={handleClose}
        queueLoading={queue.loading}
        queueError={queue.error}
        onRetry={queue.retry}
      />
    </Dialog>
  );
}

export default ReviewReturnsDialog;
